/**
 * ============================================================
 * ALTENS — MARKETING POPUP / BANDEAU
 * ============================================================
 *
 * Gestion :
 * - déclenchement au scroll
 * - déclenchement après délai
 * - exit intent desktop
 * - apparition smooth
 * - fermeture animée
 * - fréquence d'affichage
 * - limitation par session
 * - localStorage
 * - sessionStorage
 * - tracking dataLayer
 */

const initMarketingPopup = () => {
    const popup = document.querySelector("[data-marketing-popup]");

    if (!popup) {
        return;
    }

    /**
     * ========================================================
     * CONFIGURATION
     * ========================================================
     */

    const campaign = popup.dataset.campaign || "";

    const trigger = popup.dataset.trigger || "scroll";

    const scrollPercentage = Math.min(100, Math.max(1, Number(popup.dataset.scroll || 50)));

    const delay = Math.max(1, Number(popup.dataset.delay || 10));

    const hideDays = Math.max(0, Number(popup.dataset.hideDays || 7));

    const oncePerSession = popup.dataset.onceSession === "1";

    const product = popup.dataset.product || "";

    if (!campaign) {
        return;
    }

    /**
     * ========================================================
     * ÉLÉMENTS
     * ========================================================
     */

    const closeButtons = popup.querySelectorAll("[data-marketing-popup-close]");

    const cta = popup.querySelector("[data-marketing-popup-cta]");

    /**
     * ========================================================
     * STORAGE
     * ========================================================
     */

    const storageKey = `altens_popup_closed_${campaign}`;

    const sessionKey = `altens_popup_session_${campaign}`;

    /**
     * ========================================================
     * ÉTAT
     * ========================================================
     */

    let isOpen = false;

    let hasOpened = false;

    let delayTimer = null;

    let closingTimer = null;

    /**
     * ========================================================
     * TRACKING
     * ========================================================
     */

    const track = (eventName, extra = {}) => {
        window.dataLayer = window.dataLayer || [];

        window.dataLayer.push({
            event: eventName,

            popup_campaign: campaign,

            popup_product: product,

            popup_trigger: trigger,

            page_path: window.location.pathname,

            ...extra,
        });
    };

    /**
     * ========================================================
     * LOCAL STORAGE
     * ========================================================
     */

    const getClosedTimestamp = () => {
        try {
            return Number(window.localStorage.getItem(storageKey) || 0);
        } catch (error) {
            return 0;
        }
    };

    /**
     * ========================================================
     * SESSION STORAGE
     * ========================================================
     */

    const hasBeenShownThisSession = () => {
        if (!oncePerSession) {
            return false;
        }

        try {
            return window.sessionStorage.getItem(sessionKey) === "1";
        } catch (error) {
            return false;
        }
    };

    const markShownThisSession = () => {
        if (!oncePerSession) {
            return;
        }

        try {
            window.sessionStorage.setItem(sessionKey, "1");
        } catch (error) {
            // Storage indisponible.
        }
    };

    /**
     * ========================================================
     * ENREGISTREMENT DE LA FERMETURE
     * ========================================================
     */

    const markClosed = () => {
        try {
            window.localStorage.setItem(storageKey, String(Date.now()));
        } catch (error) {
            // Storage indisponible.
        }
    };

    /**
     * ========================================================
     * PEUT-ON AFFICHER LE BANDEAU ?
     * ========================================================
     */

    const canDisplay = () => {
        /**
         * Déjà ouvert sur cette page.
         */
        if (hasOpened) {
            return false;
        }

        /**
         * Déjà affiché pendant cette session.
         */
        if (hasBeenShownThisSession()) {
            return false;
        }

        /**
         * Pas de délai après fermeture.
         */
        if (hideDays <= 0) {
            return true;
        }

        const closedAt = getClosedTimestamp();

        if (!closedAt) {
            return true;
        }

        const hideDuration = hideDays * 24 * 60 * 60 * 1000;

        return Date.now() - closedAt >= hideDuration;
    };

    /**
     * ========================================================
     * SUPPRESSION DES TRIGGERS
     * ========================================================
     */

    const removeTriggers = () => {
        window.removeEventListener("scroll", handleScroll);

        document.removeEventListener("mouseout", handleExitIntent);

        if (delayTimer) {
            window.clearTimeout(delayTimer);

            delayTimer = null;
        }
    };

    /**
     * ========================================================
     * OUVERTURE
     * ========================================================
     */

    const openPopup = () => {
        if (isOpen || !canDisplay()) {
            return;
        }

        isOpen = true;

        hasOpened = true;

        /**
         * Nettoyage éventuel
         * d'une ancienne animation.
         */

        if (closingTimer) {
            window.clearTimeout(closingTimer);

            closingTimer = null;
        }

        popup.classList.remove("is-closing");

        /**
         * Double requestAnimationFrame
         * pour garantir que le navigateur
         * applique bien la transition CSS.
         */

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                popup.classList.add("is-open");
            });
        });

        popup.setAttribute("aria-hidden", "false");

        /**
         * Une seule apparition
         * pendant la session.
         */

        markShownThisSession();

        /**
         * Tracking.
         */

        track("popup_view");

        /**
         * Le bandeau est affiché :
         * plus besoin des triggers.
         */

        removeTriggers();
    };

    /**
     * ========================================================
     * FERMETURE
     * ========================================================
     */

    const closePopup = () => {
        if (!isOpen) {
            return;
        }

        isOpen = false;

        /**
         * Retire l'état ouvert et lance
         * l'animation de sortie.
         */

        popup.classList.remove("is-open");

        popup.classList.add("is-closing");

        popup.setAttribute("aria-hidden", "true");

        /**
         * Enregistre la fermeture.
         */

        markClosed();

        /**
         * Tracking.
         */

        track("popup_close");

        /**
         * Nettoyage de la classe
         * après la transition CSS.
         *
         * La transition actuelle dure
         * environ 650 ms.
         */

        closingTimer = window.setTimeout(() => {
            popup.classList.remove("is-closing");

            closingTimer = null;
        }, 700);
    };

    /**
     * ========================================================
     * SCROLL
     * ========================================================
     */

    const handleScroll = () => {
        if (!canDisplay()) {
            return;
        }

        const documentHeight = document.documentElement.scrollHeight - window.innerHeight;

        /**
         * La page ne possède pas
         * suffisamment de scroll.
         */
        if (documentHeight <= 0) {
            return;
        }

        const currentScroll = window.scrollY || window.pageYOffset || 0;

        const percentage = (currentScroll / documentHeight) * 100;

        if (percentage >= scrollPercentage) {
            openPopup();
        }
    };

    /**
     * ========================================================
     * EXIT INTENT
     * ========================================================
     */

    const handleExitIntent = (event) => {
        if (!canDisplay()) {
            return;
        }

        /**
         * Exit intent désactivé
         * sur écrans tactiles.
         */

        if (window.matchMedia("(pointer: coarse)").matches) {
            return;
        }

        /**
         * On ne déclenche que lorsque
         * la souris quitte réellement
         * le document.
         */

        if (event.relatedTarget) {
            return;
        }

        /**
         * Sortie par le haut
         * du navigateur uniquement.
         */

        if (event.clientY > 10) {
            return;
        }

        openPopup();
    };

    /**
     * ========================================================
     * CLAVIER
     * ========================================================
     */

    const handleKeydown = (event) => {
        if (!isOpen) {
            return;
        }

        if (event.key === "Escape") {
            closePopup();
        }
    };

    /**
     * ========================================================
     * BOUTONS DE FERMETURE
     * ========================================================
     */

    closeButtons.forEach((button) => {
        button.addEventListener("click", closePopup);
    });

    /**
     * ========================================================
     * CTA
     * ========================================================
     */

    if (cta) {
        cta.addEventListener("click", () => {
            track("popup_cta_click", {
                popup_cta_url: cta.href,
            });
        });
    }

    /**
     * ========================================================
     * ESCAPE
     * ========================================================
     */

    document.addEventListener("keydown", handleKeydown);

    /**
     * ========================================================
     * INITIALISATION
     * ========================================================
     */

    if (!canDisplay()) {
        return;
    }

    switch (trigger) {
        /**
         * ----------------------------------------------------
         * DÉLAI
         * ----------------------------------------------------
         */

        case "delay":
            delayTimer = window.setTimeout(() => {
                openPopup();
            }, delay * 1000);

            break;

        /**
         * ----------------------------------------------------
         * EXIT INTENT
         * ----------------------------------------------------
         */

        case "exit":
            document.addEventListener("mouseout", handleExitIntent);

            break;

        /**
         * ----------------------------------------------------
         * SCROLL
         * ----------------------------------------------------
         */

        case "scroll":

        default:
            window.addEventListener("scroll", handleScroll, {
                passive: true,
            });

            /**
             * Cas où :
             *
             * - l'utilisateur recharge la page
             *   en étant déjà plus bas ;
             *
             * - le navigateur restaure
             *   automatiquement le scroll.
             */

            handleScroll();

            break;
    }
};

export default initMarketingPopup;
